import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const QRScansContext = createContext();

export const useQRScans = () => {
  const context = useContext(QRScansContext);
  if (!context) {
    throw new Error('useQRScans must be used within a QRScansProvider');
  }
  return context;
};

export const QRScansProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for QR scans endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getQRScansBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/qr-scans/api/v1/qr-scans`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/qr-scans/api/v1/qr-scans`;
    } else {
      throw new Error('Invalid login type for QR scans access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access QR scans');
    }
  }, [loginType]);

  // ========================
  // CREATE OPERATION (POST)
  // ========================
  
  /**
   * Create a new QR scan record
   * @param {Object} scanData - Scan data to create
   * @returns {Promise<Object>} Created scan object
   */
  const createScan = useCallback(async (scanData) => {
    validateAccess();
    
    if (!scanData) {
      throw new Error('Scan data is required');
    }

    // Validate required fields
    const requiredFields = ['qr_generation_id', 'business_registry_number'];
    const missingFields = requiredFields.filter(field => !scanData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scanData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create QR scan';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  // ========================
  // UPDATE OPERATION (PUT)
  // ========================
  
  /**
   * Update an existing QR scan record
   * @param {number} scanId - Scan ID to update
   * @param {Object} updateData - Partial scan data to update
   * @returns {Promise<Object>} Updated scan object
   */
  const updateScan = useCallback(async (scanId, updateData) => {
    validateAccess();
    
    if (!scanId) {
      throw new Error('Scan ID is required');
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error('Update data is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/scan/${scanId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update QR scan';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  // ========================
  // DELETE OPERATIONS (DELETE)
  // ========================
  
  /**
   * Delete a single QR scan record
   * @param {number} scanId - Scan ID to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  const deleteScan = useCallback(async (scanId) => {
    validateAccess();
    
    if (!scanId) {
      throw new Error('Scan ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/scan/${scanId}`, {
        method: 'DELETE',
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete QR scan';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  /**
   * Delete all scans for a specific QR generation ID (bulk delete)
   * @param {string} qrGenerationId - QR generation ID
   * @returns {Promise<Object>} Deletion confirmation with count
   */
  const deleteScansbyQRGeneration = useCallback(async (qrGenerationId) => {
    validateAccess();
    
    if (!qrGenerationId) {
      throw new Error('QR generation ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/qr-generation/${encodeURIComponent(qrGenerationId)}/scans`,
        {
          method: 'DELETE',
        }
      );
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete QR scans by generation ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  // ========================
  // READ OPERATIONS (GET)
  // ========================

  /**
   * Get scan by ID
   * @param {number} scanId - Scan ID
   * @returns {Promise<Object>} Scan object
   */
  const getScanById = useCallback(async (scanId) => {
    validateAccess();
    
    if (!scanId) {
      throw new Error('Scan ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/scan/${scanId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get scan by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  /**
   * Get scans by business registry number with pagination and filters
   * @param {string} registryNumber - Business registry number
   * @param {Object} options - Query options (limit, offset, filters)
   * @returns {Promise<Object>} Paginated scans with metadata
   */
  const getScansByBusinessRegistry = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.qrGenerationId) queryParams.append('qr_generation_id', options.qrGenerationId);
      if (options.country) queryParams.append('country', options.country);
      if (options.city) queryParams.append('city', options.city);
      if (options.deviceType) queryParams.append('device_type', options.deviceType);
      if (options.browserName) queryParams.append('browser_name', options.browserName);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.hasLocation !== undefined) queryParams.append('has_location', options.hasLocation);
      if (options.hasDeviceInfo !== undefined) queryParams.append('has_device_info', options.hasDeviceInfo);

      const baseUrl = getQRScansBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch QR scans by registry';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  /**
   * Get scans by QR generation ID with pagination and filters
   * @param {string} qrGenerationId - QR generation ID
   * @param {Object} options - Query options (limit, offset, filters)
   * @returns {Promise<Object>} Paginated scans with metadata
   */
  const getScansByQRGeneration = useCallback(async (qrGenerationId, options = {}) => {
    validateAccess();
    
    if (!qrGenerationId) {
      throw new Error('QR generation ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.country) queryParams.append('country', options.country);
      if (options.city) queryParams.append('city', options.city);
      if (options.deviceType) queryParams.append('device_type', options.deviceType);
      if (options.browserName) queryParams.append('browser_name', options.browserName);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.hasLocation !== undefined) queryParams.append('has_location', options.hasLocation);
      if (options.hasDeviceInfo !== undefined) queryParams.append('has_device_info', options.hasDeviceInfo);

      const baseUrl = getQRScansBaseUrl();
      const url = `${baseUrl}/qr-generation/${encodeURIComponent(qrGenerationId)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch QR scans by generation ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  /**
   * Get statistics for business registry
   * @param {string} registryNumber - Business registry number
   * @returns {Promise<Object>} Statistics data
   */
  const getScanStatsByRegistry = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get scan statistics by registry';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  /**
   * Get statistics for QR generation ID
   * @param {string} qrGenerationId - QR generation ID
   * @returns {Promise<Object>} Statistics data
   */
  const getScanStatsByQRGeneration = useCallback(async (qrGenerationId) => {
    validateAccess();
    
    if (!qrGenerationId) {
      throw new Error('QR generation ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getQRScansBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/qr-generation/${encodeURIComponent(qrGenerationId)}/stats`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get scan statistics by QR generation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getQRScansBaseUrl]);

  // ========================
  // CONVENIENCE FILTER METHODS
  // ========================

  /**
   * Get scans by country
   * @param {string} registryNumber - Business registry number
   * @param {string} country - Country name
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered scans
   */
  const getScansByCountry = useCallback(async (registryNumber, country, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      country,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans by city
   * @param {string} registryNumber - Business registry number
   * @param {string} city - City name
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered scans
   */
  const getScansByCity = useCallback(async (registryNumber, city, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      city,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans by device type
   * @param {string} registryNumber - Business registry number
   * @param {string} deviceType - Device type (desktop, mobile, tablet)
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered scans
   */
  const getScansByDeviceType = useCallback(async (registryNumber, deviceType, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      deviceType,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans by browser
   * @param {string} registryNumber - Business registry number
   * @param {string} browserName - Browser name
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered scans
   */
  const getScansByBrowser = useCallback(async (registryNumber, browserName, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      browserName,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans by date range
   * @param {string} registryNumber - Business registry number
   * @param {string} dateFrom - Start date (YYYY-MM-DD)
   * @param {string} dateTo - End date (YYYY-MM-DD)
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered scans
   */
  const getScansByDateRange = useCallback(async (registryNumber, dateFrom, dateTo, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      dateFrom,
      dateTo,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans with location data
   * @param {string} registryNumber - Business registry number
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Scans with location data
   */
  const getScansWithLocation = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      hasLocation: true,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans with device info
   * @param {string} registryNumber - Business registry number
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Scans with device information
   */
  const getScansWithDeviceInfo = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
      hasDeviceInfo: true,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get recent scans (with time-based filtering)
   * @param {string} registryNumber - Business registry number
   * @param {number} daysBack - How many days back to look
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Recent scans
   */
  const getRecentScans = useCallback(async (registryNumber, daysBack = 7, options = {}) => {
    validateAccess();
    
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);
    const dateFromStr = dateFrom.toISOString().split('T')[0];
    
    const filterOptions = {
      ...options,
      dateFrom: dateFromStr,
    };

    return await getScansByBusinessRegistry(registryNumber, filterOptions);
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scans for specific QR and group by country
   * @param {string} qrGenerationId - QR generation ID
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Scans grouped by country
   */
  const getQRScansbyCountry = useCallback(async (qrGenerationId, options = {}) => {
    validateAccess();
    
    const filterOptions = {
      ...options,
    };

    return await getScansByQRGeneration(qrGenerationId, filterOptions);
  }, [getScansByQRGeneration, validateAccess]);

  // ========================
  // BATCH OPERATIONS
  // ========================

  /**
   * Bulk delete scans by IDs
   * @param {Array<number>} scanIds - Array of scan IDs to delete
   * @returns {Promise<Object>} Deletion results
   */
  const bulkDeleteScans = useCallback(async (scanIds) => {
    validateAccess();
    
    if (!scanIds || scanIds.length === 0) {
      throw new Error('Scan IDs array is required');
    }

    setLoading(true);
    setError(null);

    try {
      const deletePromises = scanIds.map(scanId => deleteScan(scanId));
      
      const results = await Promise.allSettled(deletePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map((r, idx) => ({
        scanId: scanIds[idx],
        error: r.reason.message,
      }));
      
      return {
        successful,
        failed,
        totalProcessed: scanIds.length,
        successCount: successful.length,
        failureCount: failed.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk delete scans';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [deleteScan, validateAccess]);

  /**
   * Bulk update scans by IDs
   * @param {Array<number>} scanIds - Array of scan IDs
   * @param {Object} updateData - Data to update for all scans
   * @returns {Promise<Object>} Update results
   */
  const bulkUpdateScans = useCallback(async (scanIds, updateData) => {
    validateAccess();
    
    if (!scanIds || scanIds.length === 0) {
      throw new Error('Scan IDs array is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatePromises = scanIds.map(scanId => 
        updateScan(scanId, updateData)
      );
      
      const results = await Promise.allSettled(updatePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map((r, idx) => ({
        scanId: scanIds[idx],
        error: r.reason.message,
      }));
      
      return {
        successful,
        failed,
        totalProcessed: scanIds.length,
        successCount: successful.length,
        failureCount: failed.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk update scans';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [updateScan, validateAccess]);

  // ========================
  // EXPORT & ANALYTICS
  // ========================

  /**
   * Export scans data (prepare for CSV/Excel export)
   * @param {string} registryNumber - Business registry number
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Data formatted for export
   */
  const exportScansData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all scans without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getScansByBusinessRegistry(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.scans.map(scan => ({
      'Scan ID': scan.scan_id,
      'QR Name': scan.qr_name || 'N/A',
      'QR Generation ID': scan.qr_generation_id,
      'Scanned Date': new Date(scan.scanned_at).toLocaleDateString(),
      'Scanned Time': new Date(scan.scanned_at).toLocaleTimeString(),
      'Country': scan.country || 'Unknown',
      'City': scan.city || 'Unknown',
      'Device Type': scan.device_type || 'Unknown',
      'OS': scan.os_name || 'Unknown',
      'Browser': scan.browser_name || 'Unknown',
      'IP Address': scan.ip_address || 'N/A',
      'User Agent': scan.user_agent || 'N/A',
      'Referrer': scan.referrer || 'Direct',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `qr_scans_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getScansByBusinessRegistry, validateAccess]);

  /**
   * Get scan summary for business
   * @param {string} registryNumber - Business registry number
   * @returns {Promise<Object>} Summary statistics
   */
  const getScanSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, recentScans] = await Promise.all([
        getScanStatsByRegistry(registryNumber),
        getScansByBusinessRegistry(registryNumber, { limit: 10 })
      ]);

      const statsData = stats.stats;

      return {
        registryNumber,
        totalScans: statsData.total_scans,
        uniqueCountries: statsData.unique_countries,
        uniqueCities: statsData.unique_cities,
        uniqueDevices: statsData.unique_devices,
        uniqueBrowsers: statsData.unique_browsers,
        scansThisMonth: statsData.scans_this_month,
        scansLastMonth: statsData.scans_last_month,
        averageScansPerDay: statsData.average_scans_per_day.toFixed(2),
        lastScanTime: statsData.last_scan_time,
        firstScanTime: statsData.first_scan_time,
        topCountries: Object.entries(statsData.scans_by_country || {})
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([country, count]) => ({ country, count })),
        topDevices: Object.entries(statsData.scans_by_device_type || {})
          .sort((a, b) => b[1] - a[1])
          .map(([device, count]) => ({ device, count })),
        topBrowsers: Object.entries(statsData.scans_by_browser || {})
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([browser, count]) => ({ browser, count })),
        recentScans: recentScans.scans,
        accessLevel: loginType,
        hasScans: statsData.total_scans > 0,
        monthOverMonthGrowth: statsData.scans_last_month > 0
          ? (((statsData.scans_this_month - statsData.scans_last_month) / statsData.scans_last_month) * 100).toFixed(1) + '%'
          : 'N/A',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get scan summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getScanStatsByRegistry, getScansByBusinessRegistry, validateAccess, loginType]);

  /**
   * Get QR scan summary
   * @param {string} qrGenerationId - QR generation ID
   * @returns {Promise<Object>} QR-specific summary
   */
  const getQRScanSummary = useCallback(async (qrGenerationId) => {
    validateAccess();

    try {
      const [stats, scans] = await Promise.all([
        getScanStatsByQRGeneration(qrGenerationId),
        getScansByQRGeneration(qrGenerationId, { limit: 10 })
      ]);

      const statsData = stats.stats;
      const qrName = scans.scans[0]?.qr_name || 'Unknown QR';

      return {
        qrGenerationId,
        qrName,
        totalScans: statsData.total_scans,
        uniqueCountries: statsData.unique_countries,
        uniqueCities: statsData.unique_cities,
        uniqueDevices: statsData.unique_devices,
        uniqueBrowsers: statsData.unique_browsers,
        scansThisMonth: statsData.scans_this_month,
        scansLastMonth: statsData.scans_last_month,
        averageScansPerDay: statsData.average_scans_per_day.toFixed(2),
        lastScanTime: statsData.last_scan_time,
        firstScanTime: statsData.first_scan_time,
        topCountries: Object.entries(statsData.scans_by_country || {})
          .sort((a, b) => b[1] - a[1])
          .map(([country, count]) => ({ country, count })),
        topDevices: Object.entries(statsData.scans_by_device_type || {})
          .sort((a, b) => b[1] - a[1])
          .map(([device, count]) => ({ device, count })),
        topBrowsers: Object.entries(statsData.scans_by_browser || {})
          .sort((a, b) => b[1] - a[1])
          .map(([browser, count]) => ({ browser, count })),
        hasScans: statsData.total_scans > 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get QR scan summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getScanStatsByQRGeneration, getScansByQRGeneration, validateAccess]);

  // ========================
  // VALIDATION HELPERS
  // ========================

  /**
   * Validate scan creation data
   * @param {Object} scanData - Scan data to validate
   * @returns {Array} Array of validation errors
   */
  const validateScanData = useCallback((scanData) => {
    const errors = [];

    if (!scanData.qr_generation_id) {
      errors.push({ field: 'qr_generation_id', message: 'QR generation ID is required' });
    }

    if (!scanData.business_registry_number) {
      errors.push({ field: 'business_registry_number', message: 'Business registry number is required' });
    }

    return errors;
  }, []);

  /**
   * Validate scan filters
   * @param {Object} filters - Filters to validate
   * @returns {Array} Array of validation errors
   */
  const validateScanFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    return errors;
  }, []);

  // ========================
  // FORMATTING HELPERS
  // ========================

  /**
   * Format scan data for display
   * @param {Object} scan - Scan object
   * @returns {Object} Formatted scan object
   */
  const formatScanData = useCallback((scan) => {
    const getTimeAgo = (timestamp) => {
      const scanDate = new Date(timestamp);
      const now = new Date();
      const diffTime = now - scanDate;
      const diffSeconds = Math.floor(diffTime / 1000);
      
      if (diffSeconds < 60) return `${diffSeconds}s ago`;
      const diffMinutes = Math.floor(diffSeconds / 60);
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    };

    return {
      ...scan,
      formattedDate: new Date(scan.scanned_at).toLocaleDateString(),
      formattedTime: new Date(scan.scanned_at).toLocaleTimeString(),
      formattedDateTime: new Date(scan.scanned_at).toLocaleString(),
      timeAgo: getTimeAgo(scan.scanned_at),
      locationText: scan.city && scan.country 
        ? `${scan.city}, ${scan.country}`
        : scan.country || scan.city || 'Location unknown',
      deviceText: scan.device_type || 'Device unknown',
      browserText: `${scan.browser_name || 'Unknown'} on ${scan.os_name || 'Unknown'}`,
      hasLocation: !!(scan.country || scan.city),
      hasDeviceInfo: !!(scan.device_type || scan.browser_name),
      ipDisplay: scan.ip_address ? scan.ip_address.substring(0, 15) : 'N/A',
    };
  }, []);

  // ========================
  // HEALTH CHECK
  // ========================

  /**
   * Health check for QR scans service
   * @returns {Promise<Object>} Service health status
   */
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/qr-scans/health`
        : `${API_BASE_URL}/admin/qr-scans/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // ========================
  // ENDPOINT INFO
  // ========================

  /**
   * Get current endpoint information for debugging
   * @returns {Object} Endpoint configuration
   */
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/qr-scans/api/v1/qr-scans`
        : `${API_BASE_URL}/admin/qr-scans/api/v1/qr-scans`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      availableOperations: {
        create: 'POST /scan',
        read: 'GET /scan/{scanId}',
        update: 'PUT /scan/{scanId}',
        delete: 'DELETE /scan/{scanId}',
        listByRegistry: 'GET /registry/{registryNumber}',
        statsByRegistry: 'GET /registry/{registryNumber}/stats',
        listByQR: 'GET /qr-generation/{qrGenerationId}',
        statsByQR: 'GET /qr-generation/{qrGenerationId}/stats',
        deleteByQR: 'DELETE /qr-generation/{qrGenerationId}/scans',
      },
    };
  }, [loginType, API_BASE_URL]);

  // ========================
  // CONTEXT VALUE
  // ========================

  const value = {
    // State
    loading,
    error,
    
    // ===== CRUD Operations =====
    // Create
    createScan,
    
    // Read
    getScanById,
    getScansByBusinessRegistry,
    getScansByQRGeneration,
    getScanStatsByRegistry,
    getScanStatsByQRGeneration,
    
    // Update
    updateScan,
    
    // Delete
    deleteScan,
    deleteScansbyQRGeneration,
    
    // ===== Batch Operations =====
    bulkDeleteScans,
    bulkUpdateScans,
    
    // ===== Filter/Search Actions =====
    getScansByCountry,
    getScansByCity,
    getScansByDeviceType,
    getScansByBrowser,
    getScansByDateRange,
    getScansWithLocation,
    getScansWithDeviceInfo,
    getRecentScans,
    getQRScansbyCountry,
    
    // ===== Summary & Analytics =====
    getScanSummary,
    getQRScanSummary,
    exportScansData,
    
    // ===== Utilities =====
    checkHealth,
    clearError,
    validateScanData,
    validateScanFilters,
    formatScanData,
    getEndpointInfo,
    
    // ===== Computed Values =====
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasScansAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <QRScansContext.Provider value={value}>
      {children}
    </QRScansContext.Provider>
  );
};